import React from 'react';
import {EnumInterpretace} from '../api';
import {sanitizeUserHtml} from './sanitizeHtml';

/**
 * Regulární výrazy zrcadlí server-side validaci v MapioDataService (MCW-28420).
 * Případnou změnu je nutné držet v synchronu s backendem.
 */
export const ODKAZ_PATTERN = /^https?:\/\/\S+$/i;
export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const TELEFON_PATTERN = /^\+?[0-9 ]{9,16}$/;

/**
 * Vrátí regulární výraz pro validaci hodnoty podle interpretace, nebo null pokud se hodnota nevaliduje
 * (prostý text / formátovaný text).
 */
export const getInterpretacePattern = (interpretace?: EnumInterpretace): RegExp | null => {
    switch (interpretace) {
        case EnumInterpretace.ODKAZ:
            return ODKAZ_PATTERN;
        case EnumInterpretace.EMAIL:
            return EMAIL_PATTERN;
        case EnumInterpretace.TELEFON:
            return TELEFON_PATTERN;
        default:
            return null;
    }
};

/**
 * Chybová hláška pro validaci hodnoty podle interpretace.
 */
export const getInterpretaceValidationMessage = (interpretace?: EnumInterpretace): string => {
    switch (interpretace) {
        case EnumInterpretace.ODKAZ:
            return 'Zadejte platný odkaz začínající http:// nebo https://.';
        case EnumInterpretace.EMAIL:
            return 'Zadejte platnou e-mailovou adresu.';
        case EnumInterpretace.TELEFON:
            return 'Zadejte platné telefonní číslo.';
        default:
            return 'Neplatná hodnota.';
    }
};

/**
 * Naformátuje hodnotu atributu podle interpretace pro zobrazení v detailu objektu.
 * - ODKAZ → odkaz otevíraný v novém okně
 * - EMAIL → mailto odkaz
 * - TELEFON → tel odkaz (pouze na mobilním zařízení, jinak prostý text)
 * Pokud hodnota neodpovídá očekávanému formátu (např. starší data), vypíše se jako prostý text.
 */
export const formatInterpretaceValue = (value: unknown, interpretace: EnumInterpretace | undefined, isMobile: boolean): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
        return value as React.ReactNode;
    }
    const text = String(value);
    const trimmed = text.trim();

    switch (interpretace) {
        case EnumInterpretace.ODKAZ:
            if (!ODKAZ_PATTERN.test(trimmed)) {
                return text;
            }
            return (
                <a href={trimmed} target="_blank" rel="noopener noreferrer">
                    {text}
                </a>
            );
        case EnumInterpretace.EMAIL:
            if (!EMAIL_PATTERN.test(trimmed)) {
                return text;
            }
            return <a href={`mailto:${trimmed}`}>{text}</a>;
        case EnumInterpretace.TELEFON:
            if (!TELEFON_PATTERN.test(trimmed)) {
                return text;
            }
            // Vytočení čísla má smysl pouze na mobilním zařízení, na desktopu zobrazíme prostý text.
            if (!isMobile) {
                return text;
            }
            return <a href={`tel:${trimmed.replace(/\s/g, '')}`}>{text}</a>;
        case EnumInterpretace.FORMATOVANYTEXT:
            // Hodnota je HTML vytvořené WYSIWYG editorem – před vykreslením ho sanitizujeme (XSS).
            return <div className={'formatted-text-value'} dangerouslySetInnerHTML={{__html: sanitizeUserHtml(text)}} />;
        default:
            // prostý text zde zobrazujeme beze změny
            return text;
    }
};
